import { Component, OnInit, OnDestroy, Input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ClientContractService } from 'src/app/services/client-contract.service';
import { ClientInformationService } from 'src/app/services/client-information.service';
import { ClientIntegrationService } from '../../../services/client-integration.service';
import { ToastService } from '../../../services/toast.service';
import { ReportService } from '../../../services/report.service';
import { Report } from '../../../models/report';
import { ClientContract } from 'src/app/models/client-contract';
import { LOSTalkerTitleService } from '../../../services/lostalker-title-service.service';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';

@Component({
  selector: 'app-client-contract-report',
  templateUrl: './client-contract-report.component.html',
  styleUrls: ['./client-contract-report.component.scss'],
  providers: [ClientIntegrationService, ReportService, ToastService, ClientInformationService, ClientContractService],
  standalone: false,
})
export class ClientContractReportComponent implements OnInit, OnDestroy {
  @Input() reportId = '';
  @Input() currentReportToShow: Report;

  clientContractGridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);

  //Constants
  _currReport: Report;
  isInTab: boolean;
  excelFileName = 'ClientContracts';
  componentDestroyed$ = new Subject<boolean>();

  //Table Filter Variables
  
  contractsList: ClientContract[];

  private serviceSubscription: Subscription;

  constructor(
    protected _clientService: ClientIntegrationService,
    protected _reportService: ReportService,
    protected _toastService: ToastService,
    protected _clientContractService: ClientContractService,
    protected _clientInformationService: ClientInformationService,
    private location: Location,
    private router: Router,
    private titleService: LOSTalkerTitleService) {
    this.contractsList = [];
  }

  ngOnInit(): void {
    this.currentReportToShow = new Report(this.currentReportToShow);
    this.clientContractGridSettings.set(this.setupGrid());

    if (this.currentReportToShow) {
      this.titleService.setUpTitle('Report - ' + this.currentReportToShow.title);

      this._currReport = this.currentReportToShow;

      if (this._currReport.exportFileName !== ''
        && this._currReport.exportFileName !== undefined) {
        this.excelFileName = this._currReport.exportFileName;
      }
    }

    if (this._currReport?.dataSproc !== '' && this._currReport?.dataSproc !== undefined) {
      this.getClientContractData();
    }
  }

  public getClientContractData() {
    this.gridLoading.set(true);

    this._reportService.getClientContractTypeQueryData(this.currentReportToShow.reportId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          this.contractsList = data;
          this.clientContractGridSettings.set(this.clientContractGridSettings().setData(this.contractsList));
        },
        complete: () => {
          this.gridLoading.set(false);
        },
      });
  }

  public goBack() {
    this.location.back();
  }

  ngOnDestroy(): void {
    if (this.serviceSubscription) {
      this.serviceSubscription.unsubscribe();
    }
  }

  navigateToClient(contracts: ClientContract[]) {
    var dataItem = contracts[0];
    //Pull real client name
    this._clientService.getClientById(dataItem.losTalkerClientId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (client) => {
          //const clientName = encodeURI(client.clientDisplayName).replace('(', '%28').replace(')', '%29');

          //Get mapping if it exists
          this._clientContractService.getIntegrationMappingsByClientContractId(dataItem.clientContractId)
            .subscribe((mapping) => {
              //If not mapped go to client page
              if (mapping == null || mapping.length > 1) {
                this.router.navigate(['/editConfigV2', client.clientInformationId]);
              }
              //If mapped go to integration page
              else {
                this.router.navigate(['/editConfigV2', client.clientInformationId, mapping[0].integrationId]);
              }
            });
        },
        error: (_error) => {
          this._toastService.toastCreate('Failed to retrieve client information.', 'Danger', { autoClose: true, keepAfterRouteChange: false });
        },
      });
  }

  formatUpdatedDate(updatedDate: string): string {
    const tmp = new Date(updatedDate);
    tmp.setMinutes(tmp.getMinutes() - tmp.getTimezoneOffset());
    return tmp.toLocaleString();
  }

  setupGrid(): GridSettings {
    return new GridSettings({
      loading: this.gridLoading,
      dataKey: 'integrationId',
      data: this.contractsList,
      fixedGrid: true,
      columns: [
        new GridColumnData({ field: 'losTalkerClientId', header: 'Client ID'}),
        new GridColumnData({ field: 'clientFullName', header: 'Name'}),
        new GridColumnData({ field: 'clientShortName', header: 'Short Name'}),
        new GridColumnData({ field: 'sfCustomerNumber', header: 'SalesForce #'}),
        new GridColumnData({ field: 'endpointName', header: 'Endpoint Name'}),
        new GridColumnData({ field: 'identifierIdValue', header: 'Identifier ID'}),
        new GridColumnData({ field: 'identifierNameValue', header: 'Identifier Name'}),
        new GridColumnData({ field: 'disabled', header: 'Disabled'}),
        new GridColumnData({ field: 'comments', header: 'Comments'}),
        new GridColumnData({ field: 'currentCompletedField', header: 'Current Completed Field'}),
        new GridColumnData({ filterable: false, field: 'updatedDate', header: 'Updated Date', type: 'date', displayFunction: (value) => this.formatUpdatedDate(value) }),
      ],
      sortMode: 'multiple',
      filter: {
        enabled: this.currentReportToShow.hasSearch,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'updatedDate',
          order: -1,
        },
      ],
      actionColumn: {
        enabled: this.currentReportToShow.hasGoToButton,
        displayFirst: true,
        onSelectionChange: this.navigateToClient,
      },
      export: {
        enabled: true,
        formats: ['csv'],
        fileName: this.currentReportToShow.exportFileName?.split('.')[0] ?? this.excelFileName,
      },
    });
  }
  

}
